import {
  autoNameDevice,
  type NameResult,
  type PlatformFields,
  parseSSEBuffer,
} from "@reflecto/client-core";
import { ApiError, createApiClient, createTransferApi } from "@reflecto/client-core/api";
import type {
  DeviceDiagnosticsStatusResponse,
  DeviceKeysResponse,
  ListDevicesResponse,
  PairInitResponse,
  PairIntent,
  PairStatusConfirmed,
  PairStatusResponse,
  SyncMessage,
} from "@reflecto/core";
import { messages } from "../i18n";
import { API_BASE, SERVER_ORIGIN } from "./config";

// ── the shared HTTP client seam (desktop slice 3, PR B-ii) ────────────────────────────────
//
// The endpoints below that duplicate `@reflecto/client-core/api` now call it instead of
// hand-rolling their own fetch. What stays here is this module's PUBLIC CONTRACT, which the
// shared client deliberately does not share: free functions taking `token` first, and an
// `Error` whose message is `body.message || "<METHOD> <path> failed (<status>)"`.
//
// Preserved rather than modernised because `apps/web`'s suite pins those message strings and
// `PairingPage.tsx` branches on one of them. A migration that changed them would be a
// behaviour change wearing a refactor's clothes.
//
// NOT every function moved. Deliberately still hand-rolled:
//   - `disconnectDevice` — succeeds on a body carrying `device_not_found`, which the server
//     sends at **401**, and the shared client synthesises `unauthorized` there on purpose
//     (three CLI commands match on it; see `api/client.ts`). It cannot see the real code.
//   - the ten file-transfer calls, web push, connectors, health, the pairing SSE stream — no
//     shared-client equivalent exists. (Diagnostics, bulk dismiss, `sendFileCtrlEnvelope` and
//     `renameDevice` were on this list until slice 11e moved them into the shared client.)

/**
 * The client for the CURRENT token, memoised so a render loop calling `dismissMessage`
 * repeatedly does not allocate one per call.
 *
 * A single entry rather than a `Map`, deliberately: this app has exactly one token at a time,
 * and a map keyed by token would retain a dead client for every re-pair in the session. Small
 * either way — the whole thing dies on reload — but an unbounded cache with a bounded number
 * of live keys is a leak with no upside.
 */
let cached: { token: string; client: ReturnType<typeof createApiClient> } | null = null;

function shared(token: string): ReturnType<typeof createApiClient> {
  if (cached?.token === token) return cached.client;
  const client = createApiClient({
    apiBase: SERVER_ORIGIN,
    getToken: () => token,
    // Unused today — this app pairs through its own flow below, which sends the same header
    // directly (`"X-Reflecto-Client-Kind": "pwa"`). Stated anyway because `clientKind` has no
    // default on purpose: a shared client must not assume its host's identity, so the value
    // has to be declared even where pairing does not currently route through it.
    clientKind: "pwa",
  });
  cached = { token, client };
  return client;
}

/**
 * Translate an `ApiError` back into this module's historical `Error`, byte-for-byte.
 *
 * `label` is the "<METHOD> <path>" prefix each function used in its own synthesised message.
 * The server's message wins when there was one — which is what `serverMessage` exists to
 * answer, since `ApiError.message` has already fallen back to `HTTP <status>` by this point
 * and cannot be distinguished from a real one.
 *
 * Anything that is not an `ApiError` passes through untouched: a `SyntaxError` from a
 * malformed 2xx body reached callers raw before this migration and still does.
 */
function toWebError(e: unknown, label: string): unknown {
  if (!(e instanceof ApiError)) return e;
  // A rejected fetch used to propagate as itself; the shared client now keeps it as `cause`.
  // Hand it back unchanged. (The extension's suite pinned this and caught the gap in slice
  // 11c; this module had the same gap since #1430 with nothing pinning it.)
  if (e.code === "network_error") return e.cause;
  return new Error(e.serverMessage || `${label} failed (${e.status})`);
}

// The three file-transfer response shapes are now declared ONCE in @reflecto/client-core
// (apiResponses.ts) and parsed by its shared validators — re-exported here so this module's
// public surface is unchanged.
export type {
  CreateTransferResponse,
  TransferStatusResponse,
  TransferUploadUrlResponse,
} from "@reflecto/client-core";

export type {
  DeviceKeysResponse,
  PairInitResponse,
  PairStatusConfirmed,
  PairStatusResponse,
  SyncMessage,
};

// --- Pairing (no auth) ---

/**
 * Derive a meaningful `deviceName` for /pair/init body and post-pair
 * PATCH /v1/devices/<self>. Delegates to the shared `autoNameDevice`
 * pipeline (Tier A: UA-CH model; Tier B: deriveDeviceName UA parser;
 * Tier C: "Web Browser" surface fallback). Both call sites use this
 * same helper so the phone sees byte-identical names at pair time and
 * after.
 *
 * `uaCh` is intentionally omitted: Tier A (UA-CH `model`) requires an
 * async `navigator.userAgentData.getHighEntropyValues(...)` call, and
 * this wrapper is synchronous, so the pipeline always lands at Tier B
 * or Tier C here.
 *
 * `existingPeerNames: []` is intentional: this call site only needs
 * the name for the pair-init body. The post-pair confirm card runs a
 * separate `autoNameDevice` call with real peer names to drive the
 * collision-disambiguation UI.
 */
export function deriveWebDeviceName(): string {
  return deriveWebAutoName([]).name;
}

/**
 * Returns the full `NameResult` (name + collision + tier) so the post-pair
 * confirm card / collision disambiguation can consume the metadata.
 * Callers pass the user's existing peer names (read from `peerDevices` in
 * storage) so `collision` reflects the actual pair set; an empty list is
 * the safe default (always `collision: false`).
 */
export function deriveWebAutoName(existingPeerNames: readonly string[]): NameResult {
  const ua = typeof navigator !== "undefined" && navigator.userAgent ? navigator.userAgent : "";
  return autoNameDevice({
    ua,
    existingPeerNames,
    tierCFallback: messages.device.webFallback,
    joinName: messages.device.nameTemplate,
  });
}

/**
 * Start a new pairing session. Server returns a 6-digit code for the user
 * to enter on Android.
 *
 * `intent` records why the code was created (spec O2). Only sent when it's
 * `"assistant"` — omitting the field for the default `"mirror"` flow
 * exercises the server's own default rather than round-tripping a
 * redundant explicit value.
 */
export async function initPairing(
  extensionPubKey: string,
  intent?: PairIntent
): Promise<PairInitResponse> {
  const res = await fetch(`${API_BASE}/pair/init`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Reflecto-Client-Kind": "pwa",
    },
    body: JSON.stringify({
      extensionPubKey,
      deviceName: deriveWebDeviceName(),
      ...(intent === "assistant" ? { intent } : {}),
    }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `POST /pair/init failed (${res.status})`);
  }
  const data = await res.json();
  if (
    typeof data.code !== "string" ||
    typeof data.extensionDeviceId !== "string" ||
    typeof data.expiresIn !== "number"
  ) {
    throw new Error("POST /pair/init: unexpected response shape");
  }
  return data as PairInitResponse;
}

/** Poll whether Android has confirmed the pairing code. */
export async function pollPairingStatus(deviceId: string): Promise<PairStatusResponse> {
  const res = await fetch(`${API_BASE}/pair/status?deviceId=${encodeURIComponent(deviceId)}`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `GET /pair/status failed (${res.status})`);
  }
  const data = await res.json();
  if (data.status === "pending") {
    return { status: "pending" };
  }
  // Terminal failure (#339): the code was redeemed but the pairing could not complete,
  // so polling on is pointless. An unrecognised reason degrades to the generic one
  // rather than reaching callers as an unknown value.
  if (data.status === "rejected") {
    return {
      status: "rejected",
      reason: data.reason === "device_cap_exceeded" ? "device_cap_exceeded" : "server_error",
    };
  }
  if (
    data.status === "confirmed" &&
    typeof data.extensionToken === "string" &&
    typeof data.phonePubKey === "string" &&
    typeof data.androidDeviceId === "string"
  ) {
    return data as PairStatusConfirmed;
  }
  throw new Error("GET /pair/status: unexpected response shape");
}

// --- Pairing SSE stream (no auth) ---

export interface PairingStream {
  /**
   * Resolves when a "confirmed" event is received. Rejects in four
   * documented cases, each with a stable message callers can branch on:
   *
   *   - Network / HTTP error:            `GET /pair/stream failed (<status>)`
   *   - Server emits an "expired" event: `Pairing stream expired`
   *   - Stream ends without confirmation: `Pairing stream closed without confirmation`
   *   - Caller invokes `abort()`:        `Pairing stream aborted`
   */
  confirmed: Promise<void>;
  /** Abort the SSE connection. Safe to call multiple times. */
  abort: () => void;
}

/**
 * Open an SSE connection to GET /pair/stream for instant pairing
 * confirmation. The server pushes a "confirmed" event when Android calls
 * POST /pair/confirm, avoiding the delay of the pairing page's polling
 * fallback loop (see `pollPairingStatus`).
 */
export function openPairingStream(deviceId: string): PairingStream {
  const controller = new AbortController();

  const confirmed = (async () => {
    let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
    try {
      const res = await fetch(`${API_BASE}/pair/stream?deviceId=${encodeURIComponent(deviceId)}`, {
        signal: controller.signal,
      });
      if (!res.ok || !res.body) {
        throw new Error(`GET /pair/stream failed (${res.status})`);
      }

      reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      for (;;) {
        const { done, value } = await reader.read();
        if (done) throw new Error("Pairing stream closed without confirmation");

        buffer += decoder.decode(value, { stream: true });
        const { parsed, remaining } = parseSSEBuffer(buffer);
        buffer = remaining;

        for (const event of parsed) {
          if (event.event === "confirmed") return;
          if (event.event === "expired") throw new Error("Pairing stream expired");
        }
      }
    } catch (err: unknown) {
      // Normalise abort to a stable Error — a raw DOMException is awkward
      // to branch on, and callers that forget `.catch()` would otherwise
      // see an unhandled rejection every time they tear the stream down.
      if (err instanceof DOMException && err.name === "AbortError") {
        throw new Error("Pairing stream aborted");
      }
      throw err;
    } finally {
      // Release the body lock on every exit path (happy return, thrown
      // error, or abort). Without this the HTTP connection is held open
      // until the browser GC's the reader; on abort, the controller
      // closes the underlying fetch but the reader lock still leaks.
      // `cancel()` is a no-op if the stream is already done and cannot
      // throw a rejection we care about, so swallow silently.
      reader?.cancel().catch(() => {});
    }
  })();

  return {
    confirmed,
    abort: () => controller.abort(),
  };
}

// --- Authenticated endpoints ---

/**
 * Dismiss a notification on the server. Notifies the paired Android device.
 *
 * Multi-device V1: `targetDeviceId` is REQUIRED — it identifies the partner
 * device the dismiss should be routed to. The server returns 400
 * `missing_target` when omitted.
 */
export async function dismissMessage(
  token: string,
  msgId: string,
  targetDeviceId: string
): Promise<void> {
  // The one non-identical detail, recorded rather than glossed: this built `?to=` with
  // `URLSearchParams`, the shared client uses `encodeURIComponent`. They differ only on a
  // space (`+` vs `%20`), and a deviceId is server-issued and alphanumeric — but "equivalent
  // for every value we produce" is a narrower claim than "identical", so it is written down.
  try {
    await shared(token).dismissMessage(msgId, targetDeviceId);
  } catch (e) {
    throw toWebError(e, `DELETE /messages/${msgId}`);
  }
}

/**
 * Bulk-dismiss notifications in a single request. Backs "Clear All" — collapses
 * what was one `DELETE /messages/:msgId` per card into one `POST /messages/dismiss`,
 * avoiding the per-card request storm that tripped infra rate-limiting on large
 * backlogs (and left cards un-dismissed). The server treats already-gone ids as a
 * graceful no-op, so the caller never needs to pre-filter stale ids.
 *
 * Multi-device V1: `targetDeviceId` is REQUIRED — see `dismissMessage`.
 */
export async function dismissMessages(
  token: string,
  msgIds: string[],
  targetDeviceId: string
): Promise<void> {
  try {
    await shared(token).dismissMessages(msgIds, targetDeviceId);
  } catch (e) {
    throw toWebError(e, "POST /messages/dismiss");
  }
}

/**
 * Send an encrypted reply to a notification. Relayed to Android via FCM.
 *
 * Multi-device V1: `targetDeviceId` is REQUIRED — see `dismissMessage`.
 */
export async function replyMessage(
  token: string,
  msgId: string,
  payload: string,
  targetDeviceId: string
): Promise<void> {
  try {
    await shared(token).replyToMessage(msgId, { payload, to: targetDeviceId });
  } catch (e) {
    throw toWebError(e, `POST /messages/${msgId}/reply`);
  }
}

/**
 * Send an encrypted fire-and-forget action to a notification. Relayed to
 * Android via FCM.
 *
 * Multi-device V1: `targetDeviceId` is REQUIRED — see `dismissMessage`.
 */
export async function actionMessage(
  token: string,
  msgId: string,
  payload: string,
  targetDeviceId: string
): Promise<void> {
  try {
    await shared(token).invokeAction(msgId, { payload, to: targetDeviceId });
  } catch (e) {
    throw toWebError(e, `POST /messages/${msgId}/action`);
  }
}

/** Request the server to disconnect (unpair) this device from its partner. */
export async function disconnectDevice(token: string): Promise<void> {
  const res = await fetch(`${API_BASE}/pair`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    // 404 device_not_found = already unpaired server-side; gate on body.error so routing-404s still throw.
    if ((body as { error?: string }).error === "device_not_found") return;
    throw new Error(body.message || `DELETE /pair failed (${res.status})`);
  }
}

/**
 * Register (or replace) this device's Web Push subscription with
 * the server. Body shape matches `PushSubscriptionJSON` exactly so
 * the caller can `JSON.stringify(subscription.toJSON())` without
 * flattening. The server stores `(endpoint, p256dh, auth)` and
 * dispatches Web Push notifications when SSE is unavailable.
 *
 * Idempotent — successive PUTs replace the previous record. Called
 * post-pair AND on `pushsubscriptionchange` rotation, so accept
 * multiple invocations without side-effects.
 */
export async function registerWebPushSubscription(
  token: string,
  subscription: PushSubscriptionJSON
): Promise<void> {
  const res = await fetch(`${API_BASE}/devices/web-push-subscription`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(subscription),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `PUT /devices/web-push-subscription failed (${res.status})`);
  }
}

/**
 * Drop this device's Web Push subscription server-side WITHOUT
 * unpairing the device. Use this for an explicit "stop sending
 * background notifications" toggle in settings — the device stays
 * paired and SSE foreground delivery keeps working.
 *
 * For full disconnect/unpair, do NOT call this — the server's
 * `DELETE /pair` triggers `unlinkDevices` which deletes the
 * `devices` row, and the `device_subscriptions` table's
 * `ON DELETE CASCADE` foreign key cleans up the subscription
 * automatically. The dispatcher's 410-Gone prune covers the
 * implicit revoke case (browser uninstall, permission withdraw).
 */
export async function unregisterWebPushSubscription(token: string): Promise<void> {
  const res = await fetch(`${API_BASE}/devices/web-push-subscription`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `DELETE /devices/web-push-subscription failed (${res.status})`);
  }
}

/**
 * Send an encrypted message to the paired Android device. Used by
 * ping/pong diagnostics. Returns void — the server response (seqId,
 * sseDelivered) is intentionally discarded since the diagnostic flow only
 * needs delivery confirmation.
 *
 * Multi-device V1: `targetDeviceId` is REQUIRED — see `dismissMessage`.
 */
export async function sendMessage(
  token: string,
  payload: string,
  msgId: string,
  targetDeviceId: string,
  signal?: AbortSignal
): Promise<void> {
  // NOT migrated to `@reflecto/client-core/api`, unlike its siblings above. The shared
  // client's `sendMessage`/`sendClip` return a PARSED `SendMessageResponse` — the CLI
  // declares that return type — whereas this one is `Promise<void>` and has never read the
  // response body at all. Routing through it would make these functions read a body no
  // caller wants, which is a real semantic change, and it is what surfaced as
  // `res.text is not a function` against this suite's response doubles. Left inline rather
  // than widening the shared client or loosening a test to fit.
  const res = await fetch(`${API_BASE}/messages`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ payload, msgId, to: targetDeviceId }),
    signal,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `POST /messages failed (${res.status})`);
  }
}

/**
 * Send an encrypted clipboard payload to a paired device via `POST /v1/clipboard`.
 * Distinct from `sendMessage` so the server delivers the inline `clipboard` FCM
 * command to Android (instead of the content-free notify nudge a phone never
 * acts on). Same `{ payload, msgId, to }` body shape.
 */
export async function sendClip(
  token: string,
  payload: string,
  msgId: string,
  targetDeviceId: string,
  signal?: AbortSignal
): Promise<void> {
  // NOT migrated to `@reflecto/client-core/api`, unlike its siblings above. The shared
  // client's `sendMessage`/`sendClip` return a PARSED `SendMessageResponse` — the CLI
  // declares that return type — whereas this one is `Promise<void>` and has never read the
  // response body at all. Routing through it would make these functions read a body no
  // caller wants, which is a real semantic change, and it is what surfaced as
  // `res.text is not a function` against this suite's response doubles. Left inline rather
  // than widening the shared client or loosening a test to fit.
  const res = await fetch(`${API_BASE}/clipboard`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ payload, msgId, to: targetDeviceId }),
    signal,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `POST /clipboard failed (${res.status})`);
  }
}

/**
 * Fetch the pubkey directory for the user's paired partner devices
 * (`GET /v1/devices/keys`). Each entry is a `deviceId` + base64 X25519
 * `publicKey`, so the caller can encrypt a clip per recipient and fan
 * it out via `sendClip`. Public keys only — the route never returns
 * notification content (preserves the E2E boundary).
 *
 * Mirrors the CLI's `ApiClient.getDeviceKeys` (`apps/cli/src/transport/api.ts`):
 * top-level shape is validated here; per-entry validation is deferred to
 * the caller's per-device try/catch so one malformed key skips that
 * device rather than aborting the whole fan-out.
 */
export async function getDeviceKeys(
  token: string,
  signal?: AbortSignal
): Promise<DeviceKeysResponse> {
  const res = await fetch(`${API_BASE}/devices/keys`, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `GET /devices/keys failed (${res.status})`);
  }
  const data = await res.json();
  if (!data || !Array.isArray(data.devices)) {
    throw new Error("GET /devices/keys: unexpected response shape");
  }
  return data as DeviceKeysResponse;
}

/**
 * Per-device diagnostics snapshot for a paired partner.
 *
 * Only `partnerPushCapable` is consumed today — whether the server holds a push route to that
 * device — but the whole snapshot is returned so the next caller doesn't add a near-duplicate
 * wrapper. Mirrors the extension's `fetchDeviceDiagnostics`.
 */
export async function fetchDeviceDiagnostics(
  token: string,
  deviceId: string,
  signal?: AbortSignal
): Promise<DeviceDiagnosticsStatusResponse> {
  try {
    return await shared(token).getDeviceDiagnostics(deviceId, signal);
  } catch (e) {
    throw toWebError(e, `GET /diagnostics/devices/${deviceId}`);
  }
}

// --- Devices (auto-rename on pair, per-device disconnect) ---

/** Rename a device. The server treats this as a self-edit — only the device's own token can rename. */
export async function renameDevice(
  token: string,
  deviceId: string,
  name: string,
  platform: PlatformFields = {}
): Promise<void> {
  try {
    await shared(token).renameDevice(deviceId, name, platform);
  } catch (e) {
    throw toWebError(e, `PATCH /devices/${deviceId}`);
  }
}

/**
 * Remove a single device from the user's set. Server enforces self-only
 * removal. Used in the disconnect flow before `DELETE /pair` so the
 * other PCs in a multi-PC pair set keep their pairing — the legacy
 * endpoint would otherwise unlink every partner of the calling device.
 */
export async function removeDevice(token: string, deviceId: string): Promise<void> {
  try {
    await shared(token).disconnectDevice(deviceId);
  } catch (e) {
    throw toWebError(e, `DELETE /devices/${deviceId}`);
  }
}

/**
 * `GET /v1/devices` — the caller's device set. Used by the pairing service to backfill
 * `androidDeviceName` when `/pair/status` omits it (slice 11d: this replaced a standalone
 * `listDevices(apiBase, token)` in `client-core/api/devices.ts` that duplicated the client's
 * own `listDevices()` method).
 */
export async function listDevices(token: string): Promise<ListDevicesResponse> {
  try {
    return await shared(token).listDevices();
  } catch (e) {
    throw toWebError(e, "GET /devices");
  }
}

// --- Remote-MCP connectors (OAuth) ---

/**
 * A connected service (remote-MCP OAuth connector) owned by this device.
 *
 * Mirrors the server's owner-facing `ConnectorView` projection
 * (`apps/server/src/routes/connectors.ts`) — camelCase wire shape, no
 * snake_case mapping. `scopes` is a space-delimited OAuth scope string
 * (e.g. "notifications:send"); `scopeDevices` is a single token ("all").
 * Hand-written (no shared/generated type exists; the Android client's
 * `ConnectorSummary` is the peer hand-write).
 */
export interface ConnectorSummary {
  id: string;
  label: string;
  scopes: string;
  /** Carried for wire-parity with the server `ConnectorView`; not surfaced in the UI yet. */
  scopeDevices: string;
  createdAt: number;
  lastUsedAt: number | null;
}

/**
 * List the remote-MCP connectors (connected services) this device owns.
 * Server scopes the list to the caller's `ownerDeviceId` via the device JWT.
 */
export async function listConnectors(token: string): Promise<ConnectorSummary[]> {
  const res = await fetch(`${API_BASE}/connectors`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `GET /connectors failed (${res.status})`);
  }
  const data = await res.json();
  // Guard `data` too: a literal `null` JSON body would throw a generic TypeError on `.connectors`.
  if (!data || !Array.isArray(data.connectors)) {
    throw new Error("GET /connectors: unexpected response shape");
  }
  // Validate every element before the cast (mirrors syncMessages): a non-string `scopes`
  // would otherwise crash the row at `scopes.split(" ")` render time, not here. `scopes` must
  // also be non-empty — a connector always carries >=1 scope, so "" is malformed (and would
  // make the row's `scopeText.length > 0` guard load-bearing rather than defensive).
  for (const c of data.connectors) {
    if (
      typeof c.id !== "string" ||
      typeof c.label !== "string" ||
      typeof c.scopes !== "string" ||
      c.scopes.trim().length === 0 ||
      typeof c.scopeDevices !== "string" ||
      typeof c.createdAt !== "number" ||
      (c.lastUsedAt !== null && typeof c.lastUsedAt !== "number")
    ) {
      throw new Error("GET /connectors: malformed connector in response");
    }
  }
  return data.connectors as ConnectorSummary[];
}

/**
 * Revoke a connector: deletes its OAuth grant + access/refresh tokens and the
 * record, so the connection 401s and cannot refresh. Server enforces
 * owner-scoped deletion (404 for a connector this device doesn't own — does not
 * leak existence).
 */
export async function revokeConnector(token: string, connectorId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/connectors/${encodeURIComponent(connectorId)}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  // res.ok covers 2xx incl. 204 No Content per the Fetch spec.
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `DELETE /connectors/${connectorId} failed (${res.status})`);
  }
}

/** Fetch messages missed while the SSE / push channel was down. */
export async function syncMessages(token: string, sinceId: number): Promise<SyncMessage[]> {
  const res = await fetch(`${API_BASE}/sync?since_id=${sinceId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `GET /sync failed (${res.status})`);
  }
  const data = await res.json();
  if (!Array.isArray(data.messages)) {
    throw new Error("GET /sync: unexpected response shape");
  }
  for (const msg of data.messages) {
    if (
      typeof msg.seqId !== "number" ||
      typeof msg.msgId !== "string" ||
      typeof msg.payload !== "string"
    ) {
      throw new Error("GET /sync: malformed message in response");
    }
  }
  return data.messages as SyncMessage[];
}

/**
 * Health check. Used to verify CORS + connectivity during dev.
 *
 * Contract:
 *   - non-2xx HTTP response → throws
 *   - 2xx with `{ ok: false }` → resolves `{ ok: false }` (server soft-degraded)
 *   - 2xx with `{ ok: true }` or empty/ambiguous body → resolves `{ ok: true }`
 *   - 2xx with a non-JSON body (proxy/CDN anomaly) → resolves `{ ok: true }`
 *     AND emits a `console.warn` so the infrastructure weirdness isn't silent
 *
 * HTTP is the authoritative signal; the body only lets the server
 * explicitly say "I'm still up but degraded".
 */
export async function checkHealth(): Promise<{ ok: boolean }> {
  // /health is unversioned — strip the `/v1` suffix.
  const origin = API_BASE.replace(/\/v1$/, "");
  const res = await fetch(`${origin}/health`);
  if (!res.ok) {
    throw new Error(`GET /health failed (${res.status})`);
  }
  // A 2xx with a non-JSON body (proxy/CDN serving plain "OK" or an
  // HTML error page) shouldn't turn into a thrown SyntaxError — HTTP
  // is the authoritative signal. But silent-swallow hides a real
  // infrastructure issue, so log it before treating as healthy.
  const data: unknown = await res.json().catch((err: unknown) => {
    console.warn("[reflecto-web] /health returned non-JSON body; treating as healthy:", err);
    return {};
  });
  const body = data as { ok?: unknown } | null;
  return { ok: Boolean(body?.ok ?? true) };
}

// --- File transfer (TASK-27 M4 — relay-only; mirrors apps/extension's
// background/api.ts transfer section verbatim, adapted to this app's own
// token/API_BASE plumbing). Called directly from page code (no SW hop — web
// has no service-worker relay for this path): the resume/upload/receive
// cores live in @reflecto/client-core and discriminate on the SAME
// `TransferApiError` + named §4.4 discriminants the extension throws, so
// docs/file-transfer-resume.md's algorithms behave identically here.
//
// Endpoint contract: part n = ciphertext concat of chunks [n*80, n*80+79].
// `expiresAt` is always a unix-ms NUMBER (never an ISO string).

/**
 * Send an encrypted `file_ctrl` envelope to a paired device via
 * `POST /v1/file-ctrl` — distinct from `sendMessage` so the server delivers
 * an inline FCM data message to Android (mirrors `sendClip`'s rationale).
 */
export async function sendFileCtrlEnvelope(
  token: string,
  payload: string,
  msgId: string,
  targetDeviceId: string,
  signal?: AbortSignal
): Promise<void> {
  try {
    await shared(token).sendFileCtrlEnvelope({ payload, msgId, to: targetDeviceId }, signal);
  } catch (e) {
    throw toWebError(e, "POST /file-ctrl");
  }
}

/**
 * The nine file-transfer lifecycle calls, and the `transferFetch` / error-mapping helpers that
 * used to sit above them, now live ONCE in `@reflecto/client-core/api` (desktop slice 11f).
 * This file's copy was byte-identical to `apps/extension/src/background/api.ts`'s on eight of
 * the nine — the comment above still says "mirrors ... verbatim", which is exactly the state
 * this replaces.
 *
 * ⚠️ TWO REAL CHANGES FOR THIS SURFACE, both adopting the extension's semantics:
 *
 *   1. `cache: "no-store"` is now forced on every transfer request. This is a BUG FIX, not a
 *      refactor. The extension added it after a hardware failure: express ETags
 *      `GET /v1/transfers/:id`, so the browser's HTTP cache made the reconcile loop's status
 *      reads conditional, the server answered `304 Not Modified`, `304` fails `res.ok`, that
 *      classifies as retryable, and the resume loop pinned in an infinite 35s retry. This app
 *      wires the SAME shared resume loop (`filetransfer/senderTransfer.web.ts` →
 *      `senderResume.ts`) from the same browser engine against the same server, so it carried
 *      the identical latent bug — unobserved, not absent.
 *   2. `fetchTransferPartStream` gains an optional `signal`. Additive; no caller passes one
 *      here yet, and omitting it behaves exactly as before.
 *
 * Exported names, arities and the `TransferApiError` contract are otherwise unchanged.
 */
export const {
  createTransfer,
  getTransfer,
  getTransferUploadUrl,
  putTransferPart,
  getTransferDownloadUrl,
  fetchTransferPartStream,
  markTransferPartUploaded,
  completeTransfer,
  deleteTransfer,
} = createTransferApi({ apiBase: API_BASE });
